import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Search,
  Menu,
  X,
  ChevronDown,
} from "lucide-react";

import { navigation } from "../mock";

const LOGO = "/assets/images/logo.png";
const ARISE_LOGO = "/assets/images/arise.jpeg";

const EMAILS = [
  "info@subeb.ak.gov.ng",
  "mails@subeb.ak.gov.ng",
  "techsupport@subeb.ak.gov.ng",
];

const PHONE = "07025328036";

const Header = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= 20) {
        setShowHeader(true);
      } else if (currentScrollY > lastScrollY.current) {
        setShowHeader(false);
      } else {
        setShowHeader(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const updatedNavigation = [];

  navigation.forEach((item) => {
    if (item.label === "Media") {
      updatedNavigation.push({
        label: "Media",
        path: "/media",
        children: [
          { label: "News", path: "/blog" },
          { label: "Image Gallery", path: "/media/images" },
          { label: "Video Gallery", path: "/media/videos" },
        ],
      });
    } else if (item.label === "Contact" || item.path === "/contact") {
      updatedNavigation.push({
        label: "Contact",
        path: "/contact",
        children: [
          { label: "Contact Us", path: "/contact" },
          { label: "Complaints", path: "/complaints-data-bank" },
        ],
      });
    } else {
      updatedNavigation.push(item);
    }

    if (item.label === "Programs") {
      updatedNavigation.push({
        label: "Recruitment",
        path: "/recruitment",
      });
    }
  });

  const hasContact = updatedNavigation.some(
    (item) => item.label === "Contact"
  );

  if (!hasContact) {
    updatedNavigation.push({
      label: "Contact",
      path: "/contact",
      children: [
        { label: "Contact Us", path: "/contact" },
        { label: "Complaints", path: "/complaints-data-bank" },
      ],
    });
  }

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[9999] bg-white shadow-sm transition-transform duration-300 ${
          showHeader ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="hidden md:block bg-[#06183f] text-white text-xs">
          <div className="max-w-[1500px] mx-auto px-6 h-[42px] flex items-center justify-between">
            <div className="flex items-center gap-8">
              <a
                href={`mailto:${EMAILS[0]}`}
                className="flex items-center gap-2 hover:text-[#ff6b00] transition-colors"
              >
                <Mail size={13} />
                {EMAILS[0]}
              </a>

              <a
                href={`tel:${PHONE}`}
                className="flex items-center gap-2 hover:text-[#ff6b00] transition-colors"
              >
                <Phone size={13} />
                {PHONE}
              </a>

              <span className="flex items-center gap-2 hover:text-[#ff6b00] transition-colors">
                <MapPin size={13} />
                Idongesit Nkanga Secretariat, Uyo, Akwa Ibom State
              </span>
            </div>

            <div className="flex items-center gap-4">
              <a
                href="https://web.facebook.com/aksubeb"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="AKSUBEB Facebook"
                className="hover:text-[#ff6b00] transition-colors"
              >
                <Facebook size={15} />
              </a>

              <a
                href="https://www.linkedin.com/company/aksubeb/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="AKSUBEB LinkedIn"
                className="hover:text-[#ff6b00] transition-colors"
              >
                <Linkedin size={15} />
              </a>

              <Twitter
                size={15}
                className="hover:text-[#ff6b00] transition-colors cursor-pointer"
              />

              <Instagram
                size={15}
                className="hover:text-[#ff6b00] transition-colors cursor-pointer"
              />
            </div>
          </div>
        </div>

        <div className="bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-[110px] md:h-[125px] flex items-center justify-center relative">
            <Link
              to="/"
              className="flex items-center justify-center gap-4 md:gap-6 w-full pr-14 lg:pr-0"
            >
              <div className="flex items-center gap-3 shrink-0">
                <img
                  src={LOGO}
                  alt="AKSUBEB"
                  className="w-[54px] h-[54px] md:w-[82px] md:h-[82px] object-contain"
                />

                <span className="h-[46px] md:h-[66px] w-[1.5px] bg-[#0b6b1d]" />

                <img
                  src={ARISE_LOGO}
                  alt="ARISE"
                  className="w-[54px] h-[54px] md:w-[82px] md:h-[82px] object-contain"
                />
              </div>

              <div className="hidden md:flex items-center justify-center overflow-hidden">
                <h1
                  className="text-[#0b6b1d] font-extrabold text-[34px] lg:text-[40px] xl:text-[44px] leading-none whitespace-nowrap tracking-[-1.5px]"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Akwa Ibom State Universal Basic Education Board
                </h1>
              </div>

              <div className="md:hidden flex-1 min-w-0">
                <h1
                  className="text-[#0b6b1d] font-black text-[30px] sm:text-[38px] leading-none tracking-[-1px] whitespace-nowrap"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  AKSUBEB
                </h1>
              </div>
            </Link>

            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden absolute right-3 w-11 h-11 flex items-center justify-center rounded-xl border border-slate-200 hover:border-[#ff6b00] hover:text-[#ff6b00] transition-colors bg-white"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>

        <div className="hidden lg:block bg-gradient-to-r from-[#041b0c] via-[#0b6b1d] to-[#ff6b00] shadow-lg">
          <div className="max-w-[1500px] mx-auto px-6">
            <div className="h-[70px] flex items-center justify-center relative">
              <nav className="flex items-center gap-10">
                {updatedNavigation.map((item) => {
                  const hasChildren =
                    item.children && item.children.length > 0;

                  const isActive =
                    location.pathname === item.path ||
                    item.children?.some(
                      (child) => location.pathname === child.path
                    );

                  return (
                    <div key={item.label} className="relative group">
                      <Link
                        to={item.path}
                        className={`flex items-center gap-1 text-[17px] font-semibold transition-all ${
                          isActive
                            ? "text-[#ffddc2]"
                            : "text-white hover:text-[#ffddc2]"
                        }`}
                      >
                        {item.label}

                        {hasChildren && (
                          <span className="font-bold text-[16px] ml-0.5">
                            +
                          </span>
                        )}
                      </Link>

                      {hasChildren && (
                        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[10000]">
                          <div className="bg-white min-w-[290px] shadow-2xl rounded-sm py-4 border-t-2 border-[#ff6b00]">
                            {item.children.map((c) => (
                              <Link
                                key={c.label}
                                to={c.path}
                                className="block px-8 py-2.5 text-[15px] text-[#1f2937] hover:text-[#ff6b00] hover:bg-orange-50 transition-colors"
                              >
                                {c.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>

              <button
                className="absolute right-0 text-white hover:text-[#ffddc2] transition-colors"
                aria-label="Search"
              >
                <Search size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="h-[110px] md:h-[235px]" />

      <div
        className={`fixed inset-0 bg-black/50 z-[9998] transition-opacity duration-300 lg:hidden ${
          mobileOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={() => setMobileOpen(false)}
      />

      <div
        className={`fixed top-0 left-0 h-full w-[320px] max-w-[85vw] bg-white z-[9999] shadow-2xl transition-transform duration-300 overflow-y-auto lg:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b bg-gradient-to-r from-[#041b0c] via-[#0b6b1d] to-[#ff6b00]">
          <div className="flex items-center gap-3">
            <img
              src={LOGO}
              alt="Logo"
              className="w-[52px] h-[52px] object-contain bg-white rounded"
            />

            <img
              src={ARISE_LOGO}
              alt="ARISE"
              className="w-[52px] h-[52px] object-contain bg-white rounded px-1"
            />
          </div>

          <button
            onClick={() => setMobileOpen(false)}
            className="w-10 h-10 rounded-lg border border-white/30 text-white flex items-center justify-center"
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        <div className="px-5 py-4 border-b">
          <h2 className="text-[28px] font-black leading-tight text-[#0b6b1d] tracking-[-1px]">
            AKSUBEB
          </h2>
        </div>

        <nav className="px-5 py-5">
          {updatedNavigation.map((item) => (
            <div key={item.label} className="border-b border-slate-100">
              <Link
                to={item.path}
                onClick={() => !item.children?.length && setMobileOpen(false)}
                className="flex items-center justify-between py-4 text-[17px] font-semibold text-slate-800 hover:text-[#ff6b00] transition-colors"
              >
                <span>{item.label}</span>

                {item.children?.length > 0 && <ChevronDown size={18} />}
              </Link>

              {item.children?.length > 0 && (
                <div className="pb-4 pl-4">
                  {item.children.map((c) => (
                    <Link
                      key={c.label}
                      to={c.path}
                      onClick={() => setMobileOpen(false)}
                      className="block py-2 text-[15px] text-slate-600 hover:text-[#ff6b00] transition-colors"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>
    </>
  );
};

export default Header;